<template>
  <div class="full-size flex-column">
    <div class="flex-row">
      <v-select
        v-if="themes"
        :items="themes.content"
        v-model="selectedTheme"
        item-text="label"
        item-value="name"
        :label="$t('wdm.views.designPanels.template.labels.theme', 'Theme')"
        :return-object="true" />
      <div style="padding-top: 12px">
        <v-btn-toggle
          v-model="responsiveMode"
          class="transparent"
          mandatory>
          <v-btn
            value="mobile"
            flat>
            <v-icon small>
              fas fa-mobile-alt
            </v-icon>
          </v-btn>

          <v-btn
            value="desktop"
            flat>
            <v-icon small>
              fas fa-desktop
            </v-icon>
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>
    <div
      class="scroll-container"
      v-if="list">
      <div
        class="item-template"
        v-for="(item, index) in filteredItems"
        :key="index"
        @dblclick="addToSheet(item)">
        <div>
          <div class="inner-item-template">
            <div class="image">
              <v-img
                aspect-ratio="2"
                contain
                :src="item.thumbnail" />
            </div>
            <div class="footer">
              <div class="icon-label">
                <v-icon small>
                  fas fa-eye
                </v-icon>
                <span>{{ item.views }}</span>
              </div>
              <div class="icon-label">
                <v-icon small>
                  fas fa-heart
                </v-icon>
                <span>{{ item.likes }}</span>
              </div>
              <v-spacer />
              <div
                class="icon-label"
                :title="$t('wdm.views.designPanels.template.tooltips.addToExcel', 'Add to Excel')">
                <v-icon
                  small
                  color="green"
                  @click="addToSheet(item)">
                  fas fa-plus-square
                </v-icon>
              </div>
              <div
                class="icon-label"
                :title="$t('wdm.views.designPanels.template.tooltips.previewItem', 'Preview item')">
                <v-icon
                  small
                  color="orange"
                  @click="preview(item)">
                  fas fa-question-circle
                </v-icon>
              </div>
            </div>
          </div>
        </div>

        <div class="item-title">
          <strong>{{ item.label }}</strong>
        </div>
      </div>
      <div v-if="filteredItems.length === 0">
        {{ $t('wdm.views.designPanels.template.labels.noResult', 'No result') }}
      </div>
    </div>

    <v-dialog v-model="showEditTemplateDialog">
      <v-card v-if="showEditTemplateDialog && currentTemplate">
        <v-card-text>
          <v-text-field
            label="label"
            v-model="currentTemplate.label" />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="saveTemplate">
            {{ $t('wdm.views.designPanels.template.buttons.save', 'Save') }}
          </v-btn>
          <v-btn @click="cancelTemplate">
            {{ $t('wdm.views.designPanels.template.buttons.cancel', 'Cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { mapState, mapActions } from 'vuex'
import { forOwn, isPlainObject } from 'lodash'
import { categoryDataSource } from 'server-model'

function readLocalFile (e) {
  return new Promise(resolve => {
    const file = e.target.files[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = function (e) {
      const contents = e.target.result
      resolve(contents)
    }
    reader.readAsDataURL(file)
  })
}

export default {
  name: 'DesignPanels',
  props: {
    dataSource: {
      type: Object,
      default: null,
      required: true
    }
  },
  asyncComputed: {
    list () {
      return this.dataSource && this.dataSource.list()
    },
    themes () {
      return categoryDataSource.list()
    }
  },
  data () {
    return {
      responsiveMode: 'desktop',
      selectedTheme: null,
      currentTemplate: null,
      showEditTemplateDialog: false
    }
  },
  computed: {
    ...mapState('office', {
      office: state => state
    }),
    filteredItems () {
      return this.list && this.list.content.filter(this.filter)
    }
  },
  methods: {
    ...mapActions('office', [
      'debouncedCommand',
      'runCommand'
    ]),
    filter (item) {
      let result = true
      if (this.selectedTheme) {
        result = item.theme === this.selectedTheme.label
      }
      if (result && this.responsiveMode) {
        result = item.responsiveMode === this.responsiveMode
      }
      return result
    },
    preview (item) {

    },
    async importConfig (e) {
      const content = await readLocalFile(e)
      this.loadBase64(content)
    },

    saveTemplate () {
      this.panels[1].items.push(this.currentTemplate)
      Vue.$localStorage.set('office_templates', this.panels[1].items)
      this.showEditTemplateDialog = false
    },
    cancelTemplate () {
      this.currentTemplate = null
      this.showEditTemplateDialog = false
    },
    addRangeToTemplate () {
      this.debouncedCommand(async (context, office) => {
        const range = office.workbook.getSelectedRange()
        const sheet = office.workbook.worksheets.getActiveWorksheet()
        sheet.load('shapes')
        range.load('values')
        // const shapes = sheet.shapes
        const cellProperties = range.getCellProperties({
          format: {
            columnWidth: true,
            rowHeight: true,
            shrinkToFit: true,
            textOrientation: true,
            useStandardHeight: true,
            useStandardWidth: true,
            verticalAlignment: true,
            wrapText: true,
            font: {
              bold: true,
              color: true,
              italic: true,
              name: true,
              underline: true,
              size: true,
              strikethrough: true,
              subscript: true,
              superscript: true,
              tintAndShade: true
            },
            fill: {
              bold: true,
              color: true,
              italic: true,
              name: true,
              underline: true,
              size: true,
              strikethrough: true,
              subscript: true,
              superscript: true,
              tintAndShade: true
            }
          }
        })
        await office.sync()
        const newTemplate = {
          label: '',
          height: range.height,
          cellProperties: cellProperties.value,
          values: range.values
        }
        this.currentTemplate = newTemplate
        this.showEditTemplateDialog = true
      })
    },
    async addToSheet (item) {
      const response = await axios.get(item.fileUrl, {
        responseType: 'arraybuffer'
      })
      const base64 = Buffer.from(response.data, 'binary').toString('base64')
      this.loadBase64(base64)
    },
    loadBase64 (base64) {
      const startIndex = base64.indexOf('base64,')
      const workbookContents = startIndex !== -1 ? base64.substr(startIndex + 7) : base64

      this.runCommand(async (context, office) => {
        const sheets = office.workbook.worksheets
        sheets.addFromBase64(
          workbookContents,
          null, // get all the worksheets
          // eslint-disable-next-line no-undef
          Excel.WorksheetPositionType.after, // insert them after the worksheet specified by the next parameter
          sheets.getActiveWorksheet() // insert them after the active worksheet
        )
        await office.sync()
      })
    },
    setProp (definition, range) {
      forOwn(definition, (value, key) => {
        if (isPlainObject(value)) {
          let rangeValue = range[key]
          if (!rangeValue) {
            rangeValue = {}
            range[key] = rangeValue
          }
          this.setProp(value, rangeValue)
        } else {
          range[key] = value
        }
      })
    }
  },
  watch: {
    themes: {
      immediate: true,
      handler (themes) {
        if (themes && themes.content) {
          this.selectedTheme = themes.content[0]
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~common-assets/scss/we";
  $color: #bcbcbc;

  .import-input {
    position: relative;

    > input {
      display: none;
    }
    .file-input {
      cursor: pointer;
    }
  }

  .scroll-container {
    display: flex;
    flex-wrap: wrap;
    height: 100%;

    .item-template {
      width: 50%;
      position: relative;
      overflow: hidden;
      box-sizing: border-box;
      padding: 5px;
      margin-bottom: 10px;
      cursor: pointer;
      @include media-breakpoint-down(xs) {
        width: 100%;
      }

      .item-title {
        margin-top: 10px;
        padding-left: 10px;
        font-weight: bold;
        flex: auto;
        @extend .text-no-wrap;
      }
      .inner-item-template {
        @extend .flex-column;
        overflow: hidden;
        background: #FFFFFF;
        height: 100%;
        border-radius: 5px 5px 3px 3px;
        box-shadow: inset 0 0 0 1px rgba(31, 31, 31, 0.17), 0 1px 2px rgba(31, 31, 31, 0.05);
        .image {
          padding: 1px;
          overflow: hidden;
        }
        .footer {
          @extend .flex-row;
          color: $color;
          height: 35px;
          padding: 10px;
          margin-top: -1px;
          line-height: 12px;
          border-bottom-right-radius: 3px;
          border-bottom-left-radius: 3px;
          box-shadow: inset 0 0 0 1px rgba(31, 31, 31, 0.17), 0 1px 2px rgba(31, 31, 31, 0.05);
          .icon-label {
            margin: 0 3px 0 0;
            @extend .flex-row;
            .v-icon {
              color: $color;
              font-size: 14px !important;
              margin: 1px 2px;
            }
            span {
              font-size: 12px;
              margin: 2px;
            }
          }
        }
      }
    }
  }
</style>
